<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import api from '@/api';
import Divider from '@/components/common/divider.vue';
import { useInsightPageName } from '@/components/home/insights/use-insight-tracking';
import PersonalSurveyProfileForm from '@/components/settings/personal-survey-profile-form.vue';
import { PersonalSurveyProfileInput } from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useLayoutVariant } from '@/utils/composables/useLayoutVariant';
import {
  toPersonalSurveyProfileInput,
  toProfileTrackingCategory,
} from '@/utils/personalSurveyProfile';
import { usePersonalSurveyProfile } from '@/utils/swr';

interface Props {
  isAlwaysOpen?: boolean;
  layout?: 'inline' | 'split';
}
const props = withDefaults(defineProps<Props>(), {
  isAlwaysOpen: false,
  layout: 'inline',
});

const layoutVariant = useLayoutVariant();

const emit = defineEmits<{
  profileSaved: [];
  close: [];
}>();

const isOpen = ref(props.isAlwaysOpen);

const profileInput = ref<PersonalSurveyProfileInput>({
  department: '',
  market_area: '',
  technology_area: '',
  technical_challenge: '',
});
const loadedProfileInput = ref<PersonalSurveyProfileInput>({
  ...profileInput.value,
});
const formValid = ref(false);

const { data: personalSurveyProfile } = usePersonalSurveyProfile(
  computed(() => true),
);
watch(
  personalSurveyProfile,
  profile => {
    if (!profile) return;
    profileInput.value = toPersonalSurveyProfileInput(profile);
    loadedProfileInput.value = toPersonalSurveyProfileInput(profile);
  },
  { immediate: true },
);

const { getCurrentPathForTracking } = useCurrentPathForTracking();
const pageName = useInsightPageName();

const saveProfile = async () => {
  await api.updatePersonalSurveyProfile(
    profileInput.value,
    {
      pageName,
      pageUrl: getCurrentPathForTracking(),
      feature: 'insight',
      layoutVariant,
    },
    toProfileTrackingCategory(loadedProfileInput.value, profileInput.value, 0),
  );
  emit('profileSaved');
};

const noInputsFilled = computed(
  () =>
    profileInput.value.department.length === 0 &&
    profileInput.value.market_area.length === 0 &&
    profileInput.value.technology_area.length === 0 &&
    profileInput.value.technical_challenge.length === 0,
);
</script>

<template>
  <div class="insights-profile" :class="{ split: layout === 'split' }">
    <div class="empty-profile">
      <div class="empty-profile-message">
        <div class="c-title-bold--m">
          業務情報を登録すると発想のヒントが届きます。
        </div>
        <div class="c-text-regular--m">
          <ul class="list">
            <li>
              あなたの専門領域や課題に合わせ、着眼点・仮説・推奨アクションを提案します。
            </li>
            <li>
              入力項目が多く、内容が詳しいほど、発想のヒントの精度が高まります。
            </li>
          </ul>
        </div>
      </div>
      <button
        v-if="!isAlwaysOpen"
        class="c-btn c-btn--small c-btn--auto"
        :class="isOpen ? 'c-outlineBtn' : 'c-btn--AnewsPrimary'"
        @click="isOpen = !isOpen"
      >
        {{ isOpen ? '閉じる' : '開いて業務情報を登録する' }}
      </button>
    </div>
    <div v-if="isOpen" class="profile-form">
      <Divider
        v-if="layout === 'inline'"
        class="full-bleed"
        margin="16px -16px 16px -16px"
      />
      <div class="c-title-bold--m info-title">業務情報</div>
      <PersonalSurveyProfileForm
        v-model="profileInput"
        v-model:valid="formValid"
      />
      <button
        class="save-button c-btn c-btn--small c-btn--auto c-btn--AnewsPrimary"
        :class="{ disabled: !formValid || noInputsFilled }"
        :disabled="!formValid || noInputsFilled"
        @click="saveProfile"
      >
        保存して発想のヒントを生成する
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.insights-profile {
  padding: 16px;
  border: 1px solid $color-gray400;
  border-radius: 4px;

  .empty-profile {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .empty-profile-message {
      display: flex;
      flex-direction: column;
    }

    .list {
      margin-left: 16px;
    }
  }

  .full-bleed {
    width: auto;
  }

  .info-title {
    margin-bottom: 16px;
  }

  .save-button {
    align-self: flex-end;
    margin: 0;

    &.disabled {
      border: none;
    }
  }

  &.split {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0;
    border: none;

    .empty-profile {
      padding: 16px;
      border-radius: 4px;
      background: $color-green100;
    }

    .profile-form {
      padding: 16px;
      border: 1px solid $color-gray400;
      border-radius: 4px;
      background: $color-white;
    }
  }
}
</style>
